import FacebookShareButton from '@/components/buttons/FacebookShareButton';
import UpgradeAccountButton from '@/components/buttons/UpgradeAccountButton';
import { QuickLoginModalHOC } from '@/components/modal/LoginModal2HOC';
import { LIBRARY_DOCUMENT_DETAIL } from '@/constant/QueryKeyName';
import { DoccenAiSubscriptionsBenefit } from '@/constant/subscription';
import { copyToClipboard } from '@/helpers/copyToClipboard';
import { recursiveRetrieveCategories } from '@/helpers/doccenLibHelpers';
import useParagraphLineClamped from '@/hooks/useLineClamped';
import useToast from '@/hooks/useToast';
import useToggle from '@/hooks/useToggle';
import { ESubscriptionPackage } from '@/interfaces/subscription.interface';
import { CircularProgress } from '@mui/material';
import { hasUserSubscriptedIdealPlan } from 'common/subscription/checkSubscriptionResource';
import { ZALO_OA_SUPPORT } from 'configs/externalLink';
import { Button } from 'design-system/components/Button';
import Divider from 'design-system/components/Divider/Divider';
import { Icon } from 'design-system/components/Icon';
import { DoccenLogo } from 'design-system/components/Images/DoccenLogo';
import Typography from 'design-system/components/Typography/Typography';
import dynamic from 'next/dynamic';
import { useContext, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { DocumentDetailContext } from 'state/document/DocumentDetailProvider';

const DynamicModalDocument = dynamic(
  () =>
    import(
      '@/components/elements/doccen-lib/content-management/modal/ModalDocument'
    )
);

const DocumentMainSection = () => {
  const { document, mutationResult, latestSubscription } = useContext(
    DocumentDetailContext
  );

  const { contentRef, maxHeight, isClamped } = useParagraphLineClamped({
    linesAllowed: 5,
    dependencies: [document?.material.description],
  });
  const [seeMore, toggleSeeMore] = useToggle();
  const toast = useToast();

  const { isLoading, mutate: handleProcessAI } = mutationResult;

  const isOwner = document?.material.isOwner;

  const parsedInitialCategories = useMemo(
    () => recursiveRetrieveCategories(0, {}, document?.material.category),
    [document?.material.category]
  );

  const maximumDocumentPageLimitCondition =
    document &&
    document.material.metadata.pageCount >
      DoccenAiSubscriptionsBenefit[ESubscriptionPackage.Premium].document
        .maxPageCountForAI;

  const maxiumDocumentSizeLimitCondition =
    document &&
    document.material.metadata.size >
      DoccenAiSubscriptionsBenefit[ESubscriptionPackage.Premium].document
        .maxFileSizeForAI;

  const doesExceedLimit =
    maximumDocumentPageLimitCondition || maxiumDocumentSizeLimitCondition;

  const hasUserSubscriptedIdealPlanValue =
    hasUserSubscriptedIdealPlan(latestSubscription) ||
    document?.material.isOwner;

  const urlShare = typeof window !== 'undefined' ? window.location.href : '';

  const buttonActions: React.ReactNode[] = [
    !hasUserSubscriptedIdealPlanValue && (
      <UpgradeAccountButton
        disableChangeWhenPackageActive
        className="w-full"
        ignoreMobile
      >
        Nâng cấp tài khoản Premium
      </UpgradeAccountButton>
    ),
    <QuickLoginModalHOC
      disabled={
        isLoading || !document?.material.metadata.pageCount || doesExceedLimit
      }
      handleClickWhenDisabledQuickLogin={() => handleProcessAI()}
    >
      <Button
        {...(isLoading
          ? {}
          : {
              startIcon: {
                name: 'blink-blink',
              },
            })}
        emphasis="medium"
        disabled={
          isLoading || !document?.material.metadata.pageCount || doesExceedLimit
        }
        className="flex w-full items-center bg-primary_blue_bg"
      >
        {isLoading ? (
          <>
            <CircularProgress size={16} className="mr-2" /> Đang phân tích...
          </>
        ) : (
          <>
            Tiếp tục với DoccenAI{' '}
            <DoccenLogo
              size="small"
              department="doccen-ai"
              iconOnly
              className="ml-1"
            />
          </>
        )}
      </Button>
    </QuickLoginModalHOC>,
    doesExceedLimit && (
      <Typography variant="helper-text" className="text-center">
        *Tài liệu này có thể không khả dụng với DoccenAI.{' '}
        <a
          href={ZALO_OA_SUPPORT}
          target="_blank"
          rel="noreferrer"
          className="text-Primary/Main"
        >
          Tìm hiểu thêm
        </a>
      </Typography>
    ),
    <QuickLoginModalHOC>
      <Button
        startIcon={{
          name: 'save',
        }}
        onClick={() => toast('Tính năng đang phát triển', 'success')}
        className="w-full"
      >
        Lưu nội dung
      </Button>
    </QuickLoginModalHOC>,
  ];

  const renderBottomContent = () => {
    return isOwner ? (
      <EditButton />
    ) : (
      <div className="flex items-center justify-center gap-2 text-Text/LightBackground/Secondary">
        <Typography variant="label">Chia sẻ:</Typography>
        <FacebookShareButton href={urlShare} />
        <Icon
          iconName="link"
          className="cursor-pointer"
          onClick={() =>
            copyToClipboard(urlShare, () =>
              toast('Đã sao chép liên kết thành công', 'success')
            )
          }
        />
      </div>
    );
  };

  return (
    <>
      <div>
        <Typography
          variant="body3"
          className="text-Text/LightBackground/Secondary"
        >
          <span>Mô tả: </span>
        </Typography>
        <Typography
          variant="body3"
          ref={contentRef}
          style={{
            maxHeight: seeMore ? 'none' : maxHeight,
            overflowY: 'hidden',
          }}
          className="relative leading-[140%] text-Text/Lightbackground/Secondary"
        >
          {document?.material.description}
          {isClamped && (
            <span
              className={`${
                !seeMore ? 'absolute bottom-0 right-0 bg-white' : ''
              }`}
            >
              {!seeMore && (
                <span className="inline text-Primary/Main">... </span>
              )}
              <span
                onClick={toggleSeeMore}
                className="inline cursor-pointer text-Primary/Main hover:underline"
              >
                {seeMore ? 'Thu gọn' : 'Xem thêm'}
              </span>
            </span>
          )}
        </Typography>
      </div>
      <Divider />
      <div className="space-y-2">
        <div className="flex justify-between gap-14">
          <Typography
            variant="body3"
            className="flex-shrink-0 text-Text/LightBackground/Secondary"
          >
            Ngành:
          </Typography>
          <Typography variant="body3" className="text-right">
            {Object.values(parsedInitialCategories)
              .map((item) => item.name)
              .join(' - ')}
          </Typography>
        </div>
        <div className="flex justify-between">
          <Typography
            variant="body3"
            className="text-Text/LightBackground/Secondary"
          >
            Số trang:
          </Typography>
          <Typography variant="body3">
            {document?.material.metadata.pageCount || 0}
          </Typography>
        </div>
      </div>
      <div className="w-full space-y-4">
        {buttonActions.map((button) => button)}
      </div>
      <Divider />
      {renderBottomContent()}
    </>
  );
};

export default DocumentMainSection;

function EditButton() {
  const [openModal, toggleModal] = useToggle();
  const { document } = useContext(DocumentDetailContext);
  const queryClient = useQueryClient();

  return (
    <>
      <Button
        startIcon={{
          name: 'edit',
        }}
        className="w-full"
        onClick={toggleModal}
      >
        Chỉnh sửa
      </Button>

      {openModal && (
        <DynamicModalDocument
          openModal={openModal}
          onClose={toggleModal}
          initialData={document?.material}
          onMutateSuccess={() =>
            queryClient.refetchQueries([
              LIBRARY_DOCUMENT_DETAIL,
              document?.material.id,
            ])
          }
        />
      )}
    </>
  );
}
